import { API_ORIGIN } from '@/constants'
import { allauthJsonHeaders, ensureAllauthCsrfToken } from '@/lib/allauth-csrf'
import { getSocialProviderCallbackUrl } from '@/constants/auth-social'

export type AllauthFlow = {
  id: string
  is_pending?: boolean
  provider?: { id: string; name: string }
}

export type AllauthSessionPayload = {
  status: number
  data?: {
    user?: {
      id: number
      username: string
      email: string
    }
    flows?: AllauthFlow[]
  }
  meta?: {
    is_authenticated?: boolean
  }
  errors?: Array<{ message?: string; code?: string }>
}

export type SocialCompletePayload = {
  success: boolean
  user?: {
    id: number
    username: string
    email: string
    has_usable_password?: boolean
  }
  access_token?: string
  refresh_token?: string
  onboarding_required?: boolean
  onboarding_redirect?: boolean
  pending_provider_signup?: boolean
  approval_pending?: boolean
  approval_rejected?: boolean
  error?: string
}

function extractAllauthError(payload: Record<string, unknown>): string | null {
  const errors = payload.errors
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0] as { message?: string }
    if (first.message) return first.message
  }
  if (typeof payload.error === 'string') return payload.error
  return null
}

export async function prepSocialAuth(signupRole?: string | null): Promise<{ success: boolean; error?: string }> {
  const body: Record<string, string | null> = {}
  if (signupRole) {
    body.signup_role = signupRole
  }

  const response = await fetch(`${API_ORIGIN}/api/accounts/social/prep`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>
  if (!response.ok) {
    return { success: false, error: extractAllauthError(data) ?? 'Failed to prepare social sign-in' }
  }
  return { success: true }
}

export async function redirectToSocialProvider(providerId: string): Promise<{ success: boolean; error?: string }> {
  const callbackUrl = getSocialProviderCallbackUrl()
  const csrfToken = await ensureAllauthCsrfToken()
  if (!csrfToken) {
    return { success: false, error: 'Failed to start social sign-in' }
  }

  // Browser fetch with redirect:manual cannot read cross-origin Location (opaque redirect).
  // POST via a real form so the browser follows the OAuth redirect to the provider.
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `${API_ORIGIN}/_allauth/browser/v1/auth/provider/redirect`
  form.style.display = 'none'

  const appendField = (name: string, value: string) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  appendField('provider', providerId)
  appendField('process', 'login')
  appendField('callback_url', callbackUrl)
  appendField('csrfmiddlewaretoken', csrfToken)

  document.body.appendChild(form)
  form.submit()
  return { success: true }
}

export async function startSocialAuth(
  providerId: string,
  options?: { signupRole?: string | null },
): Promise<{ success: boolean; error?: string }> {
  const prep = await prepSocialAuth(options?.signupRole ?? null)
  if (!prep.success) return prep
  return redirectToSocialProvider(providerId)
}

export async function fetchAllauthSession(): Promise<AllauthSessionPayload> {
  const response = await fetch(`${API_ORIGIN}/_allauth/browser/v1/auth/session`, {
    method: 'GET',
    credentials: 'include',
  })
  const payload = (await response.json().catch(() => ({}))) as AllauthSessionPayload
  return { ...payload, status: response.status }
}

export function hasPendingProviderSignup(payload: AllauthSessionPayload): boolean {
  const flows = payload.data?.flows ?? []
  return flows.some((flow) => flow.id === 'provider_signup' && flow.is_pending)
}

export async function completeSocialAuth(): Promise<SocialCompletePayload> {
  const response = await fetch(`${API_ORIGIN}/api/accounts/social/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })

  const data = (await response.json().catch(() => ({}))) as SocialCompletePayload & Record<string, unknown>

  if (response.status === 401 && data.pending_provider_signup) {
    return {
      success: false,
      pending_provider_signup: true,
      error: data.error ?? 'Provider signup incomplete',
    }
  }

  if (!response.ok) {
    return {
      success: false,
      error: (data.error as string | undefined) ?? 'Failed to complete social sign-in',
    }
  }

  return data
}

export async function submitProviderSignup(): Promise<AllauthSessionPayload> {
  const headers = await allauthJsonHeaders()
  const response = await fetch(`${API_ORIGIN}/_allauth/browser/v1/auth/provider/signup`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({}),
  })
  const payload = (await response.json().catch(() => ({}))) as AllauthSessionPayload
  return { ...payload, status: response.status }
}

export async function fetchProviderSignupState(): Promise<AllauthSessionPayload> {
  const response = await fetch(`${API_ORIGIN}/_allauth/browser/v1/auth/provider/signup`, {
    method: 'GET',
    credentials: 'include',
  })
  const payload = (await response.json().catch(() => ({}))) as AllauthSessionPayload
  return { ...payload, status: response.status }
}
